// ============================================================================
// Slim GameContext composed from modular hooks (Phase 7)
// ============================================================================
import React, { createContext, useContext, useReducer, useRef, ReactNode } from 'react';
import { GameState, Player } from '../types/game';
import type { BotProfile } from '../types/game';
import { gameReducer, initialState } from './gameReducer';
import { useGameActions } from './hooks/useGameActions';
import { useTurnActions, TurnBundle } from './hooks/useTurnActions';
import { useSettingsSync } from './hooks/useSettingsSync';
import { useOnlineLobbyIdentity } from './hooks/useOnlineLobbyIdentity';
import { useScorePersistence, ScoreSyncStatus } from './hooks/useScorePersistence';
// useDebugExposure removed in snapshot-driven flow
import { loadHostFlag } from '../utils/hostFlag';
import { getSessionPlayerId } from '../utils/sessionPlayer';
import { useEventTrimming } from './hooks/useEventTrimming';
import { setDebugDynamicContextProvider, setDebugRecorderContext } from './debug/debugLog';
import { useLocalPersistence } from './hooks/useLocalPersistence';
import { EmoteProvider } from './EmoteContext';
import { useSoundEffects } from './hooks/useSoundEffects';
import { getBuildVersion } from '../utils/buildVersion';

export { gameReducer } from './gameReducer';

// --------------------- Typy kontekstu ---------------------
interface GameContextType {
  gameState: GameState;
  localPersistenceHydrated?: boolean;
  rpcLock: { active: boolean; type: string | null; eventId: string | null; startedAt: number | null };
  withRpcLock: <T>(label: string, eventId: string | null, fn: () => Promise<T>) => Promise<T>;
  refreshSnapshot: (reason?: string) => Promise<boolean>;
  scoreSync: ScoreSyncStatus;
  addPlayer: (name: string, options?: { gameId?: string; groupToken?: string | null }) => Promise<boolean>;
  addPlayerRemote: (name: string, gameId?: string, options?: { role?: 'player' | 'viewer'; groupToken?: string | null }) => Promise<boolean>;
  addOnlinePlayer: (name: string, options?: { gameId?: string; groupToken?: string | null }) => Promise<boolean>;
  joinOnlineSelf: (name: string, options?: { gameId?: string; role?: 'player' | 'viewer'; groupToken?: string | null; assumeHost?: boolean }) => Promise<boolean>;
  addBotPlayers: (count: number, profile: BotProfile) => Promise<boolean>;
  removeBotPlayer: (botId: string) => Promise<boolean>;
  markSelfPlayer: (playerId: string | null) => void;
  reorderPlayers: (players: Player[]) => void;
  startGame: (id?: string) => void;
  startNextGame: () => Promise<string | null>;
  addScore: (playerId: string, points: number, turn?: number) => void;
  undoLastAction: () => void;
  resetGame: () => void;
  exitGame: () => void;
  leaveGame: () => Promise<boolean>;

  toggleTunnels: () => void;
  toggleRescueByFive: () => void;
  toggleZones: () => void;
  toggleZonesAvoidTunnels: () => void;
  updateRapidSettings: (rapid: import('../domain/rapid').RapidSettings) => void;
  joinGame: (gameId: string, options?: { groupToken?: string | null; skipGroupLookup?: boolean; assumeHost?: boolean }) => Promise<boolean>;
  isHost: boolean;
  toggleReady: (playerId: string, suggestedReady?: boolean) => Promise<void>;
  initOnlineLobby: (options?: { gameId?: string; groupToken?: string | null; groupId?: string | null; assumeHost?: boolean }) => void;
  finalizeOrder: () => void;
  beginOnlineGame: () => void;
  rollDiceAction: () => void;
  toggleKeepDie: (index: number) => void;
  endTurn: () => void;
  undoLastTurn?: () => void;
  currentClientPlayerId: string | null;
  pendingReadyChanges: Set<string>;
}

interface DiceContextValue {
  turnCycle?: GameState['turnCycle'];
  activePlayerId?: string;
  players: Player[];
  currentPlayerIndex: number;
  isHost: boolean;
  rollDiceAction: () => void;
  toggleKeepDie: (i: number) => void;
  endTurn: () => void;
  potential?: TurnBundle['potential'];
  bankBlockedReason?: string | null;
  rescueByFiveAvailable?: boolean;
  gameState?: GameState; // debug access (segments etc.)
}

const GameContext = createContext<GameContextType | undefined>(undefined);
const DiceContext = createContext<DiceContextValue | undefined>(undefined);

const CLIENT_BUILD = getBuildVersion();

// ============================================================================
// Provider
// ============================================================================
export function GameProvider({ children, modeHint }: { children: ReactNode; modeHint?: GameState['mode'] }) {
  const seededState = React.useMemo<GameState>(() => {
    if (modeHint === 'ONLINE') {
      return { ...initialState, mode: 'ONLINE', lobby: { phase: 'LOBBY', readyMap: {}, nextGameParticipation: {} } };
    }
    if (modeHint === 'REAL') {
      return { ...initialState, mode: 'REAL', lobby: undefined, turnCycle: undefined };
    }
    return initialState;
  }, [modeHint]);

  const [gameState, dispatch] = useReducer(gameReducer, seededState);
  const [isHost, setIsHost] = React.useState<boolean>(() => loadHostFlag());
  const [currentClientPlayerId, setCurrentClientPlayerId] = React.useState<string | null>(() => (
    modeHint === 'REAL' ? null : getSessionPlayerId()
  ));
  const [rpcLock, setRpcLock] = React.useState<{ active: boolean; type: string | null; eventId: string | null; startedAt: number | null }>({
    active: false,
    type: null,
    eventId: null,
    startedAt: null,
  });

  const lockTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const withRpcLock = React.useCallback(async <T,>(label: string, eventId: string | null, fn: () => Promise<T>): Promise<T> => {
    if (lockTimeoutRef.current) {
      clearTimeout(lockTimeoutRef.current);
      lockTimeoutRef.current = null;
    }
    setRpcLock({ active: true, type: label, eventId, startedAt: Date.now() });
    lockTimeoutRef.current = setTimeout(() => {
      setRpcLock({ active: false, type: null, eventId: null, startedAt: null });
    }, 8000);
    try {
      return await fn();
    } finally {
      if (lockTimeoutRef.current) {
        clearTimeout(lockTimeoutRef.current);
        lockTimeoutRef.current = null;
      }
      setRpcLock({ active: false, type: null, eventId: null, startedAt: null });
    }
  }, []);

  const actions = useGameActions({
    gameState,
    dispatch,
    setIsHost,
    setCurrentClientPlayerId,
    modeHint,
  });

  const settings = useSettingsSync({ gameState, dispatch, isHost, currentClientPlayerId });
  const onlineLobbyIdentity = useOnlineLobbyIdentity({
    gameState,
    dispatch,
    modeHint,
    rejoinOnlineGame: actions.joinGame,
    identity: {
      playerId: currentClientPlayerId,
      setPlayerId: setCurrentClientPlayerId,
      isHost,
      setIsHost,
    },
  });
  const lobby = onlineLobbyIdentity.lobby;

  const derived = React.useMemo(
    () => onlineLobbyIdentity.derived,
    [onlineLobbyIdentity.derived]
  );

  // Local storage hydration / autosave
  const { hydrated: localPersistenceHydrated } = useLocalPersistence({
    gameState,
    dispatch,
    isHost,
    setIsHost,
    setCurrentClientPlayerId,
    modeHint,
  });

  // Wymuszaj tryb zgodny z gałęzią routingu (REAL/ONLINE)
  React.useEffect(() => {
    if (!modeHint) return;
    if (gameState.mode !== modeHint) {
      dispatch({ type: 'SET_MODE', mode: modeHint });
      if (modeHint === 'REAL') {
        setIsHost(true);
      }
    }
  }, [gameState.mode, modeHint, dispatch, setIsHost]);

  const turn = useTurnActions({
    gameState,
    dispatch,
    currentClientPlayerId,
    setCurrentClientPlayerId,
    isHost,
    addScore: actions.addScore,
    rpcLock,
    withRpcLock,
    refreshSnapshot: onlineLobbyIdentity.sync.refreshSnapshot,
  });

  const manualRefreshSnapshot = React.useCallback((reason: string = 'manual') => (
    onlineLobbyIdentity.sync.refreshSnapshot(reason)
  ), [onlineLobbyIdentity]);

  React.useEffect(() => () => {
    if (lockTimeoutRef.current) {
      clearTimeout(lockTimeoutRef.current);
      lockTimeoutRef.current = null;
    }
  }, []);

  useEventTrimming({ gameState, dispatch });

  // LocalStorage persistence for ONLINE runtime is replaced by PartyKit snapshots/events.
  const scoreSync = useScorePersistence({ gameState, refreshSnapshot: onlineLobbyIdentity.sync.refreshSnapshot });
  useSoundEffects({ gameState });

  const providerValue = React.useMemo(
    () => ({
      gameState,
      addPlayer: actions.addPlayer,
      addPlayerRemote: actions.addPlayerRemote,
      addOnlinePlayer: actions.addOnlinePlayer,
      joinOnlineSelf: actions.joinOnlineSelf,
      addBotPlayers: actions.addBotPlayers,
      removeBotPlayer: actions.removeBotPlayer,
      markSelfPlayer: actions.markSelfPlayer,
      reorderPlayers: actions.reorderPlayers,
      startGame: actions.startGame,
      startNextGame: actions.startNextGame,
      addScore: actions.addScore,
      undoLastAction: actions.undoLastAction,
      resetGame: actions.resetGame,
      exitGame: actions.exitGame,
      leaveGame: actions.leaveGame,
      joinGame: actions.joinGame,
      rpcLock,
      withRpcLock,
      toggleTunnels: settings.toggleTunnels,
      toggleRescueByFive: settings.toggleRescueByFive,
      toggleZones: settings.toggleZones,
      toggleZonesAvoidTunnels: settings.toggleZonesAvoidTunnels,
      updateRapidSettings: settings.updateRapidSettings,
      toggleReady: lobby.toggleReady,
      initOnlineLobby: lobby.initOnlineLobby,
      finalizeOrder: lobby.finalizeOrder,
      beginOnlineGame: lobby.beginOnlineGame,
      rollDiceAction: turn.rollDice,
      toggleKeepDie: turn.toggleKeepDie,
      endTurn: turn.endTurn,
      undoLastTurn: turn.undoLastTurn,
      isHost: derived.isHost,
      currentClientPlayerId: derived.currentClientPlayerId,
      pendingReadyChanges: new Set(onlineLobbyIdentity.derived.pendingReadyChanges),
      localPersistenceHydrated,
      refreshSnapshot: manualRefreshSnapshot,
      scoreSync,
    }),
    [actions, derived, gameState, lobby, settings, turn, rpcLock, withRpcLock, localPersistenceHydrated, manualRefreshSnapshot, onlineLobbyIdentity.derived.pendingReadyChanges, scoreSync]
  );

  const activeClientPlayer = React.useMemo(() => (
    currentClientPlayerId
      ? gameState.players.find((p: Player) => p.id === currentClientPlayerId) || null
      : null
  ), [gameState.players, currentClientPlayerId]);

  React.useEffect(() => {
    setDebugRecorderContext({
      gameId: gameState.id || null,
      playerId: currentClientPlayerId,
      playerName: activeClientPlayer?.name || null,
      isHost,
      clientBuild: CLIENT_BUILD,
    });
    setDebugDynamicContextProvider(() => ({
      mode: gameState.mode,
      turnNumber: gameState.turnNumber,
      activePlayerId: gameState.turnCycle?.activePlayerId || null,
      rollCount: gameState.turnCycle?.rollCount ?? null,
      status: gameState.turnCycle?.status || null,
      lobbyPhase: gameState.lobby?.phase || null,
    }));
    if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
      window.__DEBUG_INTERNAL_STATE__ = {
        mode: gameState.mode,
        turnNumber: gameState.turnNumber,
        currentPlayerIndex: gameState.currentPlayerIndex,
        lobbyPhase: gameState.lobby?.phase || null,
        settings: {
          rapid: gameState.settings?.rapid
            ? {
                enabled: gameState.settings.rapid.enabled,
                freeThresholdMs: gameState.settings.rapid.freeThresholdMs,
                baseBlockMs: gameState.settings.rapid.baseBlockMs,
                minBlockMs: gameState.settings.rapid.minBlockMs,
                scalingMode: gameState.settings.rapid.scalingMode,
              }
            : null,
          zones: gameState.settings?.zones === true,
          zonesAvoidTunnels: gameState.settings?.zonesAvoidTunnels === true,
        },
        zones: (gameState.zones || []).map((zone) => ({
          id: zone.id,
          kind: zone.kind,
          start: zone.start,
          end: zone.end,
          points: zone.points,
          revealed: zone.revealed,
        })),
        rapidTurnMeta: gameState.rapidTurnMeta
          ? {
              activePlayerId: gameState.rapidTurnMeta.activePlayerId,
              turnStartedAt: gameState.rapidTurnMeta.turnStartedAt,
              frozenContext: gameState.rapidTurnMeta.frozenContext
                ? {
                    enabled: gameState.rapidTurnMeta.frozenContext.enabled,
                    freeThresholdMs: gameState.rapidTurnMeta.frozenContext.freeThresholdMs,
                    effectiveBlockMs: gameState.rapidTurnMeta.frozenContext.effectiveBlockMs,
                    isBot: gameState.rapidTurnMeta.frozenContext.isBot,
                  }
                : null,
            }
          : null,
        rapidPenaltyPresentation: gameState.rapidPenaltyPresentation
          ? {
              playerId: gameState.rapidPenaltyPresentation.playerId,
              delta: gameState.rapidPenaltyPresentation.delta,
              turn: gameState.rapidPenaltyPresentation.turn,
              shownAt: gameState.rapidPenaltyPresentation.shownAt,
              visibleUntil: gameState.rapidPenaltyPresentation.visibleUntil,
            }
          : null,
        players: gameState.players.map((player) => ({
          id: player.id,
          name: player.name,
          score: player.score,
          status: player.status,
          role: player.role,
        })),
        turnCycle: {
          activePlayerId: gameState.turnCycle?.activePlayerId || null,
          rollCount: gameState.turnCycle?.rollCount ?? 0,
          status: gameState.turnCycle?.status || null,
        },
      };
    }
  }, [
    gameState.id,
    gameState.mode,
    gameState.currentPlayerIndex,
    gameState.players,
    gameState.settings?.rapid,
    gameState.settings?.zones,
    gameState.settings?.zonesAvoidTunnels,
    gameState.zones,
    gameState.turnNumber,
    gameState.lobby?.phase,
    gameState.rapidTurnMeta,
    gameState.rapidPenaltyPresentation,
    gameState.turnCycle?.activePlayerId,
    gameState.turnCycle?.rollCount,
    gameState.turnCycle?.status,
    currentClientPlayerId,
    activeClientPlayer?.name,
    isHost,
  ]);

  const emotesEnabled = gameState.mode === 'ONLINE' && gameState.lobby?.phase === 'IN_GAME';

  const diceValue = React.useMemo<DiceContextValue>(() => ({
    turnCycle: gameState.turnCycle,
    activePlayerId: gameState.turnCycle?.activePlayerId,
    players: gameState.players,
    currentPlayerIndex: gameState.currentPlayerIndex,
    isHost,
    rollDiceAction: turn.rollDice,
    toggleKeepDie: turn.toggleKeepDie,
    endTurn: turn.endTurn,
    potential: turn.potential,
    bankBlockedReason: turn.bankBlockedReason,
    rescueByFiveAvailable: turn.rescueByFiveAvailable,
    gameState,
  }), [gameState, isHost, turn]);

  return (
    <GameContext.Provider value={providerValue}>
      <DiceContext.Provider value={diceValue}>
        <EmoteProvider
          gameId={gameState.id}
          playerId={currentClientPlayerId}
          playerName={activeClientPlayer?.name || null}
          enabled={emotesEnabled}
        >
          {children}
        </EmoteProvider>
      </DiceContext.Provider>
    </GameContext.Provider>
  );
}

// --------------------- Hooks ---------------------
export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) {
    throw new Error('useGame must be used within GameProvider');
  }
  return ctx;
}

export function useDice(): DiceContextValue {
  const ctx = useContext(DiceContext);
  if (!ctx) {
    throw new Error('useDice must be used within GameProvider');
  }
  return ctx;
}
